/**
 * Editors shared by the Driver's Component config form.
 *
 * These were lifted verbatim out of the process-level `TransactionControlsSettings`
 * page when Rest / Stop / Swap moved from the process onto the DRIVER workflow
 * step. The config SHAPES are unchanged — `plannedStopConfig`,
 * `immediateStopConfig`, `swapConfig` and the two resume configs still look
 * exactly as they did, they just live in `FlowComponent.settings` now. Keeping
 * the shapes identical is what lets the native sheets and ResumeCapturePage be
 * reused without a rewrite.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Button,
  Stack,
  Divider,
  Collapse,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export type FieldState = 'required' | 'optional' | 'off';

/**
 * A named evidence photo — see DRIVER_PHOTO_SLOTS_ANALYSIS.md. `uid` is stable
 * across renames and reordering so historical evidence is never orphaned.
 */
export interface PhotoSlot { uid: string; label: string; required: boolean }

export interface PlannedCfg { reasons: string[]; dateTime: FieldState }
export interface ImmediateCfg { reasons: string[]; photo: FieldState; photos: PhotoSlot[] }
export interface SwapCfg { reasons: string[] }
export interface ResumeFile { file_name?: string; file_path?: string; mime_type?: string; public_url?: string }
export interface ResumeCfg { enabled: boolean; instruction: string; photo: FieldState; maxPhotos: number; supportFile: ResumeFile | null; photos: PhotoSlot[] }

export const emptyResumeCfg = (): ResumeCfg => ({ enabled: false, instruction: '', photo: 'off', maxPhotos: 1, supportFile: null, photos: [] });

/** `FilesInterceptor('images', 10)` on all three upload endpoints. */
export const MAX_PHOTO_SLOTS = 10;

const newSlotUid = () =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Keep the legacy `photo` / `maxPhotos` pair coherent with the slots.
 *
 * Those keys are dead weight for a current app build, but they are the ONLY
 * thing a build predating slots reads — leaving them stale would make it ask for
 * the wrong number of photos. Called on save for both photo surfaces.
 */
export const syncLegacyPhotoKeys = <T extends { photo: FieldState; photos: PhotoSlot[]; maxPhotos?: number }>(cfg: T): T => {
  const photos = cfg.photos ?? [];
  if (!photos.length) return cfg;
  return {
    ...cfg,
    photo: photos.some((p) => p.required) ? 'required' : 'optional',
    ...(cfg.maxPhotos !== undefined ? { maxPhotos: photos.length } : {}),
  };
};

/** Editable list of reason "points". */
export const ReasonsEditor: React.FC<{ reasons: string[]; onChange: (r: string[]) => void }> = ({ reasons, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...reasons, v]);
    setDraft('');
  };
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Reasons the driver picks from (a custom note field is always shown in the app too).
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {reasons.map((r, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={r}
              onChange={(e) => {
                const next = [...reasons];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <IconButton size="small" color="error" onClick={() => onChange(reasons.filter((_, j) => j !== i))}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        {reasons.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            No predefined reasons — the app will show only the custom reason field.
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Add a reason…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={add}>
            Add
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * Named evidence photos. With slots defined the driver gets one titled capture
 * tile per entry instead of an anonymous "up to N photos" bucket, and the title
 * is stored on the file so it captions the thumbnail everywhere it is read back.
 *
 * Empty = legacy mode: the app falls back to the unlabelled `photo`/`maxPhotos`
 * pair, which is what every process that predates this did and what an older app
 * build still reads.
 */
export const PhotoSlotsEditor: React.FC<{
  photos: PhotoSlot[];
  onChange: (p: PhotoSlot[]) => void;
  /** Shown when no slots are defined, to explain what legacy mode will do. */
  legacyHint: string;
}> = ({ photos, onChange, legacyHint }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const label = draft.trim();
    if (!label || photos.length >= MAX_PHOTO_SLOTS) return;
    onChange([...photos, { uid: newSlotUid(), label, required: true }]);
    setDraft('');
  };
  const patch = (i: number, next: Partial<PhotoSlot>) =>
    onChange(photos.map((p, j) => (j === i ? { ...p, ...next } : p)));
  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Give each photo a title the driver sees while capturing it. The title is stored with the photo and
        shown against it in the transaction history.
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {photos.map((p, i) => (
          <Box key={p.uid} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Stack sx={{ mr: 0.5 }}>
              <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} sx={{ p: 0.25 }}>
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" disabled={i === photos.length - 1} onClick={() => move(i, 1)} sx={{ p: 0.25 }}>
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
            <TextField
              size="small"
              fullWidth
              value={p.label}
              placeholder="Photo title"
              onChange={(e) => patch(i, { label: e.target.value })}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={p.required ? 'required' : 'optional'}
              onChange={(_, v) => v && patch(i, { required: v === 'required' })}
              color="success"
            >
              <ToggleButton value="required" sx={{ px: 1, textTransform: 'none' }}>Required</ToggleButton>
              <ToggleButton value="optional" sx={{ px: 1, textTransform: 'none' }}>Optional</ToggleButton>
            </ToggleButtonGroup>
            <IconButton size="small" color="error" onClick={() => onChange(photos.filter((_, j) => j !== i))}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        {photos.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            {legacyHint}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Add a photo title… e.g. Odometer"
            value={draft}
            disabled={photos.length >= MAX_PHOTO_SLOTS}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={add} disabled={photos.length >= MAX_PHOTO_SLOTS}>
            Add
          </Button>
        </Box>
        {photos.length >= MAX_PHOTO_SLOTS && (
          <Typography variant="caption" color="text.disabled">
            Maximum of {MAX_PHOTO_SLOTS} photos per action.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

/** Required / Optional / Off selector for a field. */
export const FieldStateSelect: React.FC<{ label: string; value: FieldState; onChange: (v: FieldState) => void }> = ({ label, value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
    <Typography fontWeight={600}>{label}</Typography>
    <ToggleButtonGroup size="small" exclusive value={value} onChange={(_, v) => v && onChange(v)} color="success">
      <ToggleButton value="required">Required</ToggleButton>
      <ToggleButton value="optional">Optional</ToggleButton>
      <ToggleButton value="off">Off</ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

export const subCardSx = { p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' } as const;

/**
 * Configures the capture screen shown when the driver taps Resume from a Rest
 * or Stop: an instruction (+ optional support file) and an image upload.
 */
export const ResumeCaptureEditor: React.FC<{
  cfg: ResumeCfg;
  onChange: (c: ResumeCfg) => void;
  uploadAsset: (file: File, path: string) => Promise<any>;
  showToast: (msg: string, type: 'success' | 'error') => void;
}> = ({ cfg, onChange, uploadAsset, showToast }) => {
  const [uploading, setUploading] = useState(false);
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAsset(file, 'txn-actions/resume-support');
      const f = res?.data ?? res;
      onChange({ ...cfg, supportFile: { file_name: f?.file_name, file_path: f?.file_path, mime_type: f?.mime_type, public_url: f?.public_url } });
    } catch {
      showToast('Failed to upload support file', 'error');
    } finally {
      setUploading(false);
    }
  };
  return (
    <Box sx={subCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ pr: 2 }}>
          <Typography fontWeight={700}>Capture on resume</Typography>
          <Typography variant="caption" color="text.secondary">
            Show an instruction and/or photo upload when the driver taps Resume.
          </Typography>
        </Box>
        <Switch checked={cfg.enabled} onChange={(e) => onChange({ ...cfg, enabled: e.target.checked })} color="success" />
      </Box>
      <Collapse in={cfg.enabled} unmountOnExit>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Instruction"
            placeholder="e.g. Photograph the vehicle and odometer before resuming."
            value={cfg.instruction}
            onChange={(e) => onChange({ ...cfg, instruction: e.target.value })}
          />
          <Divider />
          <Box>
            <Typography fontWeight={600} mb={0.5}>Photos on resume</Typography>
            <PhotoSlotsEditor
              photos={cfg.photos ?? []}
              onChange={(photos) => onChange({ ...cfg, photos })}
              legacyHint="No titled photos — the driver just gets an untitled camera tile, using the setting below."
            />
          </Box>
          {/* The legacy pair only governs capture while no slots are defined.
              With slots, it is derived on save (syncLegacyPhotoKeys) purely so an
              older app build still asks for the right number of photos. */}
          {(cfg.photos ?? []).length === 0 && (
            <>
              <FieldStateSelect label="Photo on resume" value={cfg.photo} onChange={(photo) => onChange({ ...cfg, photo })} />
              {cfg.photo !== 'off' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography fontWeight={600}>Max photos</Typography>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: MAX_PHOTO_SLOTS }}
                    sx={{ width: 96 }}
                    value={cfg.maxPhotos}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      onChange({ ...cfg, maxPhotos: isNaN(n) ? 1 : Math.min(Math.max(n, 1), MAX_PHOTO_SLOTS) });
                    }}
                  />
                </Box>
              )}
            </>
          )}
          <Divider />
          <Box>
            <Typography fontWeight={600} mb={0.5}>Support file (optional)</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              A PDF or image shown with the instruction (e.g. a checklist).
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" component="label" disabled={uploading}>
                {uploading ? 'Uploading…' : cfg.supportFile?.file_name ? 'Replace file' : 'Upload file'}
                <input hidden type="file" accept="application/pdf,image/*" onChange={onPickFile} />
              </Button>
              {cfg.supportFile?.file_name && (
                <>
                  <Typography variant="caption" sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cfg.supportFile.file_name}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => onChange({ ...cfg, supportFile: null })}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </Stack>
      </Collapse>
    </Box>
  );
};

